import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import WorkflowSidebar from './components/WorkflowSidebar';
import MethodCard from './components/MethodCard';
import CancelButton from './components/CancelButton';
import apiClient from '../../api/client';
import SaveResultsWizard from '../../components/chemistry/SaveResultsWizard';
import StructureImage from '../../components/chemistry/StructureImage';
import { useWorkbenchContext } from '../../hooks/useWorkbenchContext';
import WorkbenchContextBar from '../../components/workbench/WorkbenchContextBar';
import ToolSwitcher from '../../components/workbench/ToolSwitcher';
import { useToolStep } from '../../hooks/useWorkbenchContext';
import * as $3Dmol from '3dmol';

const STEPS = [
  { name: 'Input' },
  { name: 'Configure' },
  { name: 'Generate' },
  { name: 'Results' },
];

/* ---------- 3D Conformer Viewer ---------- */
const ConformerViewer: React.FC<{ conformers: any[]; selectedIdx: number }> = ({ conformers, selectedIdx }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<any>(null);
  const [spinning, setSpinning] = useState(false);

  useEffect(() => {
    if (!containerRef.current || conformers.length === 0) return;
    const conf = conformers[selectedIdx] || conformers[0];
    const molblock = conf?.sdf_text || conf?.molblock || '';
    if (!molblock) return;
    const timer = setTimeout(() => {
      if (!containerRef.current) return;
      if (viewerRef.current) {
        viewerRef.current.removeAllModels();
      } else {
        viewerRef.current = ($3Dmol as any).createViewer(containerRef.current, { backgroundColor: '#1a1d22', antialias: true });
      }
      viewerRef.current.addModel(molblock, 'sdf');
      viewerRef.current.setStyle({}, { stick: { radius: 0.12, colorscheme: 'Jmol' }, sphere: { scale: 0.25, colorscheme: 'Jmol' } });
      viewerRef.current.zoomTo();
      viewerRef.current.render();
    }, 50);
    return () => clearTimeout(timer);
  }, [conformers, selectedIdx]);

  const toggleSpin = () => {
    if (!viewerRef.current) return;
    viewerRef.current.spin(spinning ? false : 'y');
    setSpinning(!spinning);
  };

  return (
    <div style={{ position: 'relative', marginBottom: 16 }}>
      <div ref={containerRef} style={{ width: '100%', height: 360, borderRadius: 8, border: '1px solid var(--card-border)', overflow: 'hidden' }} />
      <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ fontSize: 11, color: '#a7abb6', background: 'rgba(0,0,0,0.5)', borderRadius: 4, padding: '2px 6px' }}>
          Conf {(conformers[selectedIdx]?.conf_id ?? selectedIdx + 1)}
        </span>
        <button onClick={toggleSpin} title={spinning ? 'Stop rotation' : 'Rotate'}
          style={{ padding: 4, borderRadius: 4, backgroundColor: spinning ? 'rgba(13,148,136,0.3)' : 'rgba(0,0,0,0.4)', color: spinning ? '#5eead4' : '#a7abb6', border: 'none', cursor: 'pointer', fontSize: 11 }}>
          &#x21bb;
        </button>
      </div>
    </div>
  );
};

const WorkbenchConformers: React.FC = () => {
  const { smiles: ctxSmiles, moleculeId: ctxMoleculeId, fileId: ctxFileId, projectId: ctxProjectId } = useWorkbenchContext();
  const [step, setStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [smiles, setSmiles] = useState(ctxSmiles || '');
  const [count, setCount] = useState(10);
  const [forceField, setForceField] = useState<'uff' | 'mmff94' | 'mmff94s'>('mmff94');
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');
  const abortRef = useRef<AbortController | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [selectedConf, setSelectedConf] = useState(0);
  const { bailOutText, setHasSaved } = useToolStep();

  const runGeneration = async () => {
    setIsRunning(true);
    setError('');
    setResult(null);
    setSelectedConf(0);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const method = forceField === 'uff' ? 'uff' : 'mmff';
      const resp = await apiClient.post('/v2/conformers/generate', {
        smiles, num_confs: count, method,
      }, { signal: controller.signal });
      const data = resp.data;
      setResult(data);
      setCompletedSteps((prev) => Array.from(new Set([...prev, 2])));
      setStep(3);
    } catch (err: any) {
      if (err.name === 'AbortError') setError('Generation cancelled');
      else setError(err.message || 'Generation failed');
    } finally {
      setIsRunning(false);
      abortRef.current = null;
    }
  };

  return (
    <>
    <WorkbenchContextBar />
    <div style={{ display: 'flex', height: 'calc(100vh - 84px)', background: 'var(--content-bg)' }}>
      <WorkflowSidebar steps={STEPS} currentStep={step} completedSteps={completedSteps} onStepClick={setStep} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={statusBarStyle}>
          <Link to="/hubs" style={{ color: '#f59e0b', textDecoration: 'none' }}>Toolbox</Link>
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <ToolSwitcher currentToolId="conformers" />
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <span>Engine: RDKit + {forceField.toUpperCase()}</span>
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <span>Step {step + 1}/{STEPS.length}</span>
          <div style={{ flex: 1 }} />
          <CancelButton onCancel={() => abortRef.current?.abort()} isRunning={isRunning} />
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 24, display: 'flex', gap: 20 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {step === 0 && (
              <div>
                <h2 style={h2Style}>Enter Molecule</h2>
                <p style={descStyle}>Input molecule for conformer generation.</p>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
                  <button onClick={() => setSmiles('c1ccc(CC(=O)O)cc1')} style={btnStyle}>Phenylacetic acid <StructureImage smiles="c1ccc(CC(=O)O)cc1" size={24} /></button>
                  <button onClick={() => setSmiles('CC(=O)Oc1ccccc1C(=O)O')} style={btnStyle}>Aspirin <StructureImage smiles="CC(=O)Oc1ccccc1C(=O)O" size={24} /></button>
                </div>
                <input type="text" value={smiles} onChange={(e) => setSmiles(e.target.value)}
                  placeholder="Enter molecule notation..."
                  style={{ ...textareaStyle, height: 40 }} />
                <button onClick={() => { if (smiles.trim()) { setCompletedSteps((prev) => Array.from(new Set([...prev, 0]))); setStep(1); } }}
                  disabled={!smiles.trim()} style={{ ...primaryBtnStyle, marginTop: 12, opacity: smiles.trim() ? 1 : 0.5 }}>Continue</button>
              </div>
            )}

            {step === 1 && (
              <div>
                <h2 style={h2Style}>Configure Generation</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 400 }}>
                  <div>
                    <label style={{ color: 'var(--text-muted)', fontSize: 13, display: 'block', marginBottom: 6 }}>
                      Number of conformers: <span style={{ color: '#f59e0b', fontWeight: 600 }}>{count}</span>
                    </label>
                    <input type="range" min={1} max={200} value={count}
                      onChange={(e) => setCount(parseInt(e.target.value))}
                      style={{ width: '100%', accentColor: '#f59e0b' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-secondary)', fontSize: 11 }}>
                      <span>1</span><span>200</span>
                    </div>
                  </div>
                  <div>
                    <label style={{ color: 'var(--text-muted)', fontSize: 13, display: 'block', marginBottom: 6 }}>Force field:</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {([['mmff94', 'MMFF94'], ['mmff94s', 'MMFF94s'], ['uff', 'UFF']] as const).map(([val, label]) => (
                        <button key={val} onClick={() => setForceField(val as any)}
                          style={{ ...btnStyle, ...(forceField === val ? { background: 'rgba(245,158,11,0.15)', borderColor: '#f59e0b', color: '#f59e0b' } : {}) }}>
                          {label}
                        </button>
                      ))}
                    </div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: 11, marginTop: 6 }}>
                      {forceField === 'uff' && 'Universal — fast, works for any element, less accurate for organics'}
                      {forceField === 'mmff94' && 'Merck Molecular — best for drug-like molecules (recommended)'}
                      {forceField === 'mmff94s' && 'Static variant of MMFF94 — better for energy minimisation'}
                    </div>
                  </div>
                </div>
                <button onClick={() => { setCompletedSteps((prev) => Array.from(new Set([...prev, 1]))); setStep(2); }}
                  style={{ ...primaryBtnStyle, marginTop: 20 }}>Continue</button>
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 style={h2Style}>Generate Conformers</h2>
                <p style={descStyle}>
                  Molecule: <StructureImage smiles={smiles} size={32} /><br />
                  Method: {forceField.toUpperCase()} | Count: {count}
                </p>
                {!isRunning && <button onClick={runGeneration} style={primaryBtnStyle}>Generate Conformers</button>}
                {isRunning && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={spinnerStyle} /><span style={{ color: '#f59e0b' }}>Generating {count} conformers...</span>
                  </div>
                )}
                {error && <div style={errorStyle}>{error}</div>}
              </div>
            )}

            {step === 3 && result && (
              <div>
                <h2 style={h2Style}>Conformer Results ({result.num_confs || 0} generated)</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8, marginBottom: 16 }}>
                  <div style={resultCardStyle}>
                    <div style={resultLabelStyle}>Requested</div>
                    <div style={resultValueStyle}>{result.requested_confs}</div>
                  </div>
                  <div style={resultCardStyle}>
                    <div style={resultLabelStyle}>Generated</div>
                    <div style={resultValueStyle}>{result.num_confs}</div>
                  </div>
                  <div style={resultCardStyle}>
                    <div style={resultLabelStyle}>Method</div>
                    <div style={{ ...resultValueStyle, fontSize: 14 }}>{result.method || forceField.toUpperCase()}</div>
                  </div>
                  <div style={resultCardStyle}>
                    <div style={resultLabelStyle}>Force Field</div>
                    <div style={{ ...resultValueStyle, fontSize: 14 }}>{result.forcefield || forceField.toUpperCase()}</div>
                  </div>
                </div>

                {/* 3D Viewer */}
                {(result.conformers || result.results || []).length > 0 && (
                  <ConformerViewer conformers={result.conformers || result.results || []} selectedIdx={selectedConf} />
                )}

                {(result.conformers || result.results || []).length > 0 && (
                  <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--card-border)' }}>
                          <th style={thStyle}>Conf ID</th>
                          <th style={thStyle}>Energy (kcal/mol)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(result.conformers || result.results || []).map((conf: any, i: number) => (
                          <tr key={i} onClick={() => setSelectedConf(i)}
                            style={{ borderBottom: '1px solid var(--card-bg)', cursor: 'pointer', background: selectedConf === i ? 'rgba(245,158,11,0.08)' : undefined }}>
                            <td style={{ ...tdStyle, fontWeight: selectedConf === i ? 700 : undefined }}>{conf.conf_id}</td>
                            <td style={{ ...tdStyle, color: '#f59e0b' }}>{conf.energy_kcal_mol?.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => {
                    const confs = result.conformers || result.results || [];
                    const sdfParts = confs.map((c: any) => {
                      const block = c.sdf_text || c.molblock || '';
                      return block.trimEnd().endsWith('$$$$') ? block : block.trimEnd() + '\n$$$$';
                    });
                    const blob = new Blob([sdfParts.join('\n')], { type: 'chemical/x-sdf' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a'); a.href = url; a.download = 'conformers.sdf'; a.click(); URL.revokeObjectURL(url);
                  }} style={primaryBtnStyle}>Download SDF</button>
                  <button onClick={() => {
                    const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a'); a.href = url; a.download = 'conformers.json'; a.click(); URL.revokeObjectURL(url);
                  }} style={btnStyle}>Download JSON</button>
                  <button onClick={() => {
                    const confs = result.conformers || result.results || [];
                    const lines = ['conf_id,energy_kcal_mol', ...confs.map((c: any) => `${c.conf_id},${c.energy_kcal_mol}`)];
                    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a'); a.href = url; a.download = 'conformers.csv'; a.click(); URL.revokeObjectURL(url);
                  }} style={btnStyle}>Download CSV</button>
                  <button onClick={() => setSaveOpen(true)} style={btnStyle}>Save to Project</button>
                </div>
                <SaveResultsWizard isOpen={saveOpen} onClose={() => setSaveOpen(false)} rawData={result} toolName="Conformers" toolKey="conformer" smiles={ctxSmiles || smiles} moleculeId={ctxMoleculeId} fileId={ctxFileId} projectId={ctxProjectId} />
              </div>
            )}
          </div>

          <div style={{ width: 320, flexShrink: 0 }}>
            <MethodCard engine="RDKit" title="ETKDGv3 Conformer Generation"
              description="Distance geometry with experimental torsion-angle preferences (version 3). Generates diverse low-energy 3D conformations, then optimises each with the selected force field."
              parameters={[
                { name: 'n', desc: 'Number of conformers to generate' },
                { name: 'MMFF94', desc: 'Merck Molecular Force Field — Halgren (1996) — parameterised for organic/drug-like molecules, higher accuracy' },
                { name: 'MMFF94s', desc: 'Static variant of MMFF94 — better for energy minimisation, no out-of-plane bending' },
                { name: 'UFF', desc: 'Universal Force Field — Rapp\u00e9 et al (1992) — fast, covers all elements, less accurate for organics' },
              ]}
              reference="Riniker & Landrum (2015) J Chem Inf Model 55:2562-2574"
              referenceUrl="https://doi.org/10.1021/acs.jcim.5b00654"
              source="RDKit AllChem.EmbedMultipleConfs + MMFF94/UFF optimization" />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

const statusBarStyle: React.CSSProperties = { padding: '8px 20px', background: 'var(--hover-bg)', borderBottom: '1px solid var(--card-border)', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 8 };
const h2Style: React.CSSProperties = { color: '#f5f5f5', fontSize: 18, fontWeight: 600, marginBottom: 12, marginTop: 0 };
const descStyle: React.CSSProperties = { color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 };
const btnStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer', fontWeight: 500 };
const primaryBtnStyle: React.CSSProperties = { ...btnStyle, background: '#f59e0b', color: 'var(--content-bg)', border: '1px solid #f59e0b', fontWeight: 600 };
const textareaStyle: React.CSSProperties = { width: '100%', padding: '10px 14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, color: 'var(--text-primary)', fontSize: 14, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' };
const errorStyle: React.CSSProperties = { marginTop: 10, padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#fb7185', fontSize: 13 };
const spinnerStyle: React.CSSProperties = { width: 20, height: 20, border: '3px solid var(--card-border)', borderTopColor: '#f59e0b', borderRadius: '50%', animation: 'spin 0.8s linear infinite' };
const resultCardStyle: React.CSSProperties = { background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: '12px 14px' };
const resultLabelStyle: React.CSSProperties = { color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 };
const resultValueStyle: React.CSSProperties = { color: '#f59e0b', fontSize: 18, fontWeight: 700, fontFamily: 'monospace' };
const thStyle: React.CSSProperties = { padding: '8px 12px', textAlign: 'left', color: '#a7abb6', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' };
const tdStyle: React.CSSProperties = { padding: '6px 12px', color: 'var(--text-primary)', fontFamily: 'monospace', fontSize: 13 };

export default WorkbenchConformers;
